import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiRequest } from '@/features/videos-common/api/client';
import { HOMEPAGE_VIDEOS_KEY, nextVideoKey } from './queryKeys';

/**
 * Response shapes of the public home-video endpoints. Field names match the
 * steward-api Swagger docs (components.schemas.HomeVideo, HomepageVideo and
 * HomeVideoResource) exactly.
 */

/**
 * Companion resource; null on the video when it has no resource url.
 *
 * @typedef {Object} HomeVideoResource
 * @property {string|null} title
 * @property {string|null} description
 * @property {string} url
 */

/**
 * A published video. `homePosition` is only present on GET /homepage-videos
 * items (1 is the featured card); videos from GET /videos/next omit it.
 *
 * @typedef {Object} HomeVideo
 * @property {number} id
 * @property {string} youtubeVideoId
 * @property {string} title
 * @property {string|null} description
 * @property {number|null} durationSeconds
 * @property {string|null} thumbnailUrl - display only, never sent to the server
 * @property {number} [homePosition]
 * @property {string|null} transcript
 * @property {number|null} readTimeMinutes
 * @property {HomeVideoResource|null} resource
 */

/**
 * An item of GET /homepage-videos: a HomeVideo plus the video that follows
 * it in the user's audience playlist (the same answer as GET /videos/next),
 * or null.
 *
 * @typedef {HomeVideo & { next?: HomeVideo|null }} HomepageVideo
 */

export const MAX_HOME_VIDEOS = 5;

/**
 * Orders by homePosition and caps at the section's maximum. The server
 * already does both; this keeps the layout correct if it ever does not.
 *
 * @param {HomepageVideo[]|null|undefined} videos
 * @returns {HomepageVideo[]}
 */
export function normalizeHomepageVideos(videos) {
  if (!Array.isArray(videos)) return [];
  return [...videos]
    .sort((a, b) => (a.homePosition ?? Infinity) - (b.homePosition ?? Infinity))
    .slice(0, MAX_HOME_VIDEOS);
}

/**
 * GET /homepage-videos. Nothing is requested while `enabled` is false.
 *
 * Takes no parameters: the server derives the user's audience. Each item
 * carries its `next` video, which is written into the GET /videos/next cache
 * so opening a home video needs no second request.
 * Failures are logged and leave `data` undefined; the section renders nothing.
 *
 * @param {{ enabled?: boolean }} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<HomepageVideo[]>}
 */
export default function useHomepageVideos(options = {}) {
  const { enabled = true } = options;
  const request = useApiRequest();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: HOMEPAGE_VIDEOS_KEY,
    queryFn: async () => normalizeHomepageVideos(await request('/homepage-videos')),
    enabled,
    retry: false,
    onSuccess: (videos) => {
      videos.forEach((video) => {
        if (video.next === undefined) return;
        queryClient.setQueryData(nextVideoKey(video.youtubeVideoId), { next: video.next });
      });
    },
    onError: (error) => {
      console.error('Failed to load home page videos', error);
    }
  });
}
